import { useCallback, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { useThemeStore } from "@/store/themeStore";

/** White / very light UI surfaces — standard logo ink. */
const URL_CHAIN_LIGHT = ["/brand/logo-full.svg"];

/** Dark or saturated backgrounds (e.g. hero gradients). Falls back to light assets if inverted files are missing. */
const URL_CHAIN_DARK = [
  "/brand/logo-full-inverted.svg",
  "/brand/logo-full.svg",
];

type AppBrandLogoProps = {
  alt?: string;
  /**
   * `light` — white or light gray surfaces (sidebar, mobile header, form column).
   * `dark` — non-white / colored backdrops (e.g. gradient hero); uses `logo-full-inverted.*` first.
   */
  background?: "light" | "dark";
  /** Wrapper (layout / sizing container). */
  className?: string;
  /** Applied to the inner `img`. */
  imgClassName?: string;
};

type AppBrandLogoInnerProps = {
  alt: string;
  background: "light" | "dark";
  chain: readonly string[];
  className?: string;
  imgClassName?: string;
};

function AppBrandLogoInner({
  alt,
  background,
  chain,
  className,
  imgClassName,
}: AppBrandLogoInnerProps) {
  const [index, setIndex] = useState(0);

  const onError = useCallback(() => {
    setIndex((i) => i + 1);
  }, []);

  const exhausted = index >= chain.length;
  const src = exhausted ? undefined : chain[index];

  if (!src) {
    return (
      <div
        className={cn("min-h-8 rounded-lg bg-surface-200", className)}
        role="img"
        aria-label={alt}
      />
    );
  }

  return (
    <div className={cn(className)}>
      <img
        key={`${background}-${src}`}
        src={src}
        alt={alt}
        className={cn(
          "h-full w-full max-w-full object-contain object-left",
          imgClassName,
        )}
        onError={onError}
      />
    </div>
  );
}

/**
 * Full brand logo from `public/brand/`. Use `background="dark"` on colored gradients;
 * default `light` uses `logo-full.svg`.
 */
export function AppBrandLogo({
  alt = "Flitrip",
  background = "light",
  className,
  imgClassName,
}: AppBrandLogoProps) {
  const resolvedTheme = useThemeStore((s) => s.resolved);
  // "light" means the app surface, which is dark in dark theme.
  const effectiveBackground =
    background === "light" && resolvedTheme === "dark" ? "dark" : background;
  const chain = useMemo(
    () => (effectiveBackground === "dark" ? URL_CHAIN_DARK : URL_CHAIN_LIGHT),
    [effectiveBackground],
  );

  return (
    <AppBrandLogoInner
      key={effectiveBackground}
      alt={alt}
      background={effectiveBackground}
      chain={chain}
      className={className}
      imgClassName={imgClassName}
    />
  );
}
